import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { Send, Loader2, Phone, Mail, Clock, MessageCircle, MapPin } from "lucide-react";
import { buildWhatsAppUrl } from "../lib/contact";
import { translations } from "../i18n/translations";

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=10770+Columbia+Pike+Ste+300+%231313+Silver+Spring+MD+20901";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  phone: "",
  email: "",
  service_type: "ac_installation",
  zip_code: "",
  message: "",
};

export const QuoteForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, language: lang });
      toast.success(t.quote.success);
      setForm(initialForm);
    } catch (err) {
      toast.error(t.quote.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 px-4 rounded-xl bg-ink border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-heat focus:ring-2 focus:ring-heat/30 transition";

  const serviceOptions = Object.entries(t.quote.services);

  // Build a short English WhatsApp message using any data already typed.
  const buildPrefilledMessage = () => {
    const hasAny = form.name || form.zip_code || form.message;
    if (!hasAny) return translations.en.whatsappMessage;
    const serviceLabel = translations.en.quote.services[form.service_type] || "";
    const parts = ["Hi Aarón!"];
    if (form.name) parts.push(`I'm ${form.name}.`);
    parts.push(`Need: ${serviceLabel}.`);
    if (form.zip_code) parts.push(`ZIP ${form.zip_code}.`);
    if (form.message) parts.push(form.message);
    return parts.join(" ");
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-ink-deep">
      <div className="absolute inset-x-0 top-0 divider-temp" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left side info */}
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-cool">{t.quote.eyebrow}</span>
            <h2 className="mt-4 font-heading font-bold text-white tracking-tight text-4xl sm:text-5xl leading-[1.05]" data-testid="contact-title">
              {t.quote.title}
            </h2>
            <p className="mt-4 text-white/70 text-base sm:text-lg">{t.quote.subtitle}</p>

            <div className="mt-10 space-y-4">
              <a
                href="tel:+12407618778"
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-ink-card/60 hover:bg-ink-card p-5 transition card-warm"
                data-testid="contact-phone"
              >
                <div className="h-12 w-12 rounded-xl bg-heat/15 ring-1 ring-heat/40 flex items-center justify-center">
                  <Phone className="text-heat" size={20} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/55">Phone</div>
                  <div className="font-heading font-bold text-white text-lg">240-761-8778</div>
                </div>
              </a>
              <a
                href={buildWhatsAppUrl(buildPrefilledMessage())}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-ink-card/60 hover:bg-ink-card p-5 transition"
                data-testid="contact-whatsapp"
                style={{ boxShadow: "0 0 0 1px rgba(37, 211, 102, 0)" }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 0 0 1px rgba(37,211,102,0.5), 0 18px 40px rgba(37,211,102,0.18)")}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 0 0 1px rgba(37, 211, 102, 0)")}
              >
                <div className="h-12 w-12 rounded-xl bg-[#25D366]/15 ring-1 ring-[#25D366]/40 flex items-center justify-center">
                  <MessageCircle className="text-[#25D366]" size={20} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/55">WhatsApp</div>
                  <div className="font-heading font-bold text-white text-lg">240-761-8778</div>
                </div>
              </a>
              <a
                href="mailto:aaronhvacllc@gmail.com"
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-ink-card/60 hover:bg-ink-card p-5 transition card-cool"
                data-testid="contact-email"
              >
                <div className="h-12 w-12 rounded-xl bg-cool/15 ring-1 ring-cool/40 flex items-center justify-center">
                  <Mail className="text-cool" size={20} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/55">Email</div>
                  <div className="font-heading font-bold text-white text-lg break-all">aaronhvacllc@gmail.com</div>
                </div>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-ink-card/60 p-5">
                <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Clock className="text-white" size={20} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/55">Hours</div>
                  <div className="font-heading font-bold text-white text-lg">Mon–Sat · 7AM – 8PM</div>
                </div>
              </div>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 rounded-2xl border border-white/10 bg-ink-card/60 hover:bg-ink-card p-5 transition card-cool"
                data-testid="contact-address"
              >
                <div className="h-12 w-12 shrink-0 rounded-xl bg-cool/15 ring-1 ring-cool/40 flex items-center justify-center">
                  <MapPin className="text-cool" size={20} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/55">Address</div>
                  <div className="font-heading font-semibold text-white text-base leading-snug mt-0.5">
                    10770 Columbia Pike Ste 300 #1313<br />Silver Spring, MD 20901
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-3xl bg-ink-card border border-white/10 p-6 sm:p-9"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/55 mb-2">{t.quote.labels.name}</label>
                <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="quote-input-name" placeholder="John Doe" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/55 mb-2">{t.quote.labels.phone}</label>
                <input required value={form.phone} onChange={update("phone")} type="tel" className={inputCls} data-testid="quote-input-phone" placeholder="(240) 761-8778" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-white/55 mb-2">{t.quote.labels.email}</label>
                <input required value={form.email} onChange={update("email")} type="email" className={inputCls} data-testid="quote-input-email" placeholder="you@email.com" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/55 mb-2">{t.quote.labels.service}</label>
                <select value={form.service_type} onChange={update("service_type")} className={inputCls} data-testid="quote-input-service">
                  {serviceOptions.map(([k, label]) => (
                    <option key={k} value={k} className="bg-ink-card">{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/55 mb-2">{t.quote.labels.zip}</label>
                <input required value={form.zip_code} onChange={update("zip_code")} className={inputCls} data-testid="quote-input-zip" placeholder="20910" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-white/55 mb-2">{t.quote.labels.message}</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  className="w-full px-4 py-3 rounded-xl bg-ink border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-heat focus:ring-2 focus:ring-heat/30 transition resize-none"
                  data-testid="quote-input-message"
                  placeholder=" "
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-btn"
              className="mt-7 w-full h-14 rounded-full btn-heat font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-70"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              {loading ? t.quote.labels.sending : t.quote.labels.submit}
            </button>

            <p className="mt-4 text-center text-xs text-white/55">{t.advantage.promiseBadge}</p>
          </form>
        </div>
      </div>
    </section>
  );
};
